import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft, Bot, RotateCcw } from 'lucide-react';
import confetti from 'canvas-confetti';
import { generatePlayerNumbers, getBackgroundState, getBackgroundColor } from '@/lib/gameUtils';
import NumberCard from './NumberCard';
import { useSound } from '@/hooks/useSound';
import { useHaptic } from '@/hooks/useHaptic';

// Viral 6-7 Victory Animation Component
const SixSevenAnimation = () => {
  return (
    <div className="flex items-center justify-center gap-6 my-8 h-40 overflow-visible">
      <motion.span
        className="text-8xl md:text-9xl font-black text-chaos-green leading-none"
        style={{
          textShadow: '0 0 30px hsl(var(--chaos-green) / 0.8), 0 0 60px hsl(var(--chaos-green) / 0.5)'
        }}
        animate={{
          y: [-30, 30, -30],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >
        6
      </motion.span>
      <motion.span
        className="text-8xl md:text-9xl font-black text-chaos-green leading-none"
        style={{
          textShadow: '0 0 30px hsl(var(--chaos-green) / 0.8), 0 0 60px hsl(var(--chaos-green) / 0.5)'
        }}
        animate={{
          y: [30, -30, 30],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >
        7
      </motion.span>
    </div>
  );
};

type MusicIntensity = 'calm' | 'waiting' | 'medium' | 'intense' | 'critical' | 'victory' | 'defeat';

interface PracticeModeProps {
  onBack: () => void;
  setMusicIntensity: (intensity: MusicIntensity) => void;
}

interface RoundResult {
  round: number;
  playerChoice: number;
  aiChoice: number;
  previousTotal: number;
  newTotal: number;
  movedCloser: boolean;
}

export default function PracticeMode({ onBack, setMusicIntensity }: PracticeModeProps) {
  const [currentTotal, setCurrentTotal] = useState(0);
  const [currentRound, setCurrentRound] = useState(1);
  const [targetTotal] = useState(67);
  const [gameStatus, setGameStatus] = useState<'playing' | 'won' | 'lost'>('playing');
  const [playerNumbers, setPlayerNumbers] = useState<[number, number]>([0, 0]);
  const [aiNumbers, setAiNumbers] = useState<[number, number]>([0, 0]);
  const [hasSelected, setHasSelected] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [roundHistory, setRoundHistory] = useState<RoundResult[]>([]);
  const [lastRoundResult, setLastRoundResult] = useState<RoundResult | null>(null);

  // Calculate if we're in "close call" territory (within 3 points)
  const distanceToTarget = targetTotal - currentTotal;
  const isCloseCall = distanceToTarget > 0 && distanceToTarget <= 3 && gameStatus === 'playing';
  const wasCloseCallRef = useRef(false);

  const { playSound } = useSound();
  const { vibrate } = useHaptic();

  // Play tension sound and haptic when entering close call
  useEffect(() => {
    if (isCloseCall && !wasCloseCallRef.current) {
      playSound('closecall', 0.4);
      vibrate('tension');
      wasCloseCallRef.current = true;
    } else if (!isCloseCall) {
      wasCloseCallRef.current = false;
    }
  }, [isCloseCall, playSound, vibrate]);

  // Heartbeat haptic during close call
  useEffect(() => {
    if (!isCloseCall) return;
    
    const interval = setInterval(() => {
      vibrate('heartbeat');
    }, 800);
    
    return () => clearInterval(interval);
  }, [isCloseCall, vibrate]);

  // Generate numbers for both player and AI with strategic generation after round 4
  const generateNewRound = useCallback(() => {
    const isOvershoot = currentTotal > targetTotal;
    // Pass currentTotal and targetTotal for strategic generation after round 4
    setPlayerNumbers(generatePlayerNumbers(currentRound, isOvershoot, undefined, currentTotal, targetTotal));
    setAiNumbers(generatePlayerNumbers(currentRound, isOvershoot, undefined, currentTotal, targetTotal));
    setHasSelected(false);
    setShowResults(false);
  }, [currentRound, currentTotal, targetTotal]);

  useEffect(() => {
    generateNewRound();
  }, [currentRound]);

  // Set music intensity based on game state
  useEffect(() => {
    if (gameStatus === 'won') {
      setMusicIntensity('victory');
    } else if (gameStatus === 'lost') {
      setMusicIntensity('defeat');
    } else {
      const diff = Math.abs(currentTotal - targetTotal);
      if (diff <= 5) {
        setMusicIntensity('critical');
      } else if (diff <= 15) {
        setMusicIntensity('intense');
      } else {
        setMusicIntensity('medium');
      }
    }
  }, [currentTotal, targetTotal, gameStatus, setMusicIntensity]);

  // AI strategy: pick the number that gets closer to 67
  const getAIChoice = useCallback(() => {
    const [num1, num2] = aiNumbers;
    const diff1 = Math.abs((currentTotal + num1) - targetTotal);
    const diff2 = Math.abs((currentTotal + num2) - targetTotal);
    
    // 70% chance to pick optimal, 30% chance to pick random (to add chaos)
    if (Math.random() < 0.7) {
      return diff1 <= diff2 ? num1 : num2;
    }
    return Math.random() < 0.5 ? num1 : num2;
  }, [aiNumbers, currentTotal, targetTotal]);

  const handleSelectNumber = async (number: number) => {
    if (hasSelected || gameStatus !== 'playing') return;
    setHasSelected(true);
    
    playSound('select');
    vibrate('selection');

    // Get AI choice
    const aiChoice = getAIChoice();
    
    // Calculate new total
    const previousTotal = currentTotal;
    const roundSum = number + aiChoice;
    const newTotal = previousTotal + roundSum;
    
    const movedCloser = Math.abs(newTotal - targetTotal) < Math.abs(previousTotal - targetTotal);

    const result: RoundResult = {
      round: currentRound,
      playerChoice: number,
      aiChoice,
      previousTotal,
      newTotal,
      movedCloser,
    };

    setLastRoundResult(result);
    setRoundHistory(prev => [...prev, result]);
    setShowResults(true);

    // Wait for animation
    await new Promise(resolve => setTimeout(resolve, 2000));

    setCurrentTotal(newTotal);

    // Check win/lose conditions
    if (newTotal === targetTotal) {
      setGameStatus('won');
      playSound('win', 0.5);
      vibrate('victory');
      // Trigger confetti
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b']
      });
    } else if (currentRound >= 15 || (newTotal > targetTotal + 30)) {
      setGameStatus('lost');
      playSound('failure');
      vibrate('error');
    } else {
      if (movedCloser) {
        playSound('success', 0.3);
        vibrate('success');
      } else {
        playSound('failure', 0.2);
        vibrate('warning');
      }
      setCurrentRound(prev => prev + 1);
    }
  };

  const resetGame = () => {
    setCurrentTotal(0);
    setCurrentRound(1);
    setGameStatus('playing');
    setRoundHistory([]);
    setLastRoundResult(null);
    setHasSelected(false);
    setShowResults(false);
  };

  const backgroundState = getBackgroundState(currentTotal, targetTotal);

  if (gameStatus !== 'playing') {
    return (
      <div className="min-h-screen bg-background p-4 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full space-y-6 text-center"
        >
          {gameStatus === 'won' ? (
            <>
              <SixSevenAnimation />
              <h1 className="text-5xl font-black text-chaos-green">
                YOU WIN!
              </h1>
            </>
          ) : (
            <>
              <div className="text-8xl">💀</div>
              <h1 className="text-5xl font-black text-chaos-red">
                GAME OVER
              </h1>
            </>
          )}

          <div className="glass-card p-6 rounded-2xl space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-muted-foreground">Final Total</div>
                <div className="text-3xl font-black text-primary">{currentTotal}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Rounds</div>
                <div className="text-3xl font-black">{currentRound}</div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <Button
              onClick={resetGame}
              className="h-14 text-lg font-bold glow-green"
            >
              <RotateCcw className="mr-2 h-5 w-5" />
              Play Again
            </Button>
            <Button
              onClick={onBack}
              variant="outline"
              className="h-12 font-bold"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Menu
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-500 ${getBackgroundColor(backgroundState)} p-4 relative`}>
      {/* Close Call Animation - Pulsing Border */}
      <AnimatePresence>
        {isCloseCall && (
          <>
            {/* Pulsing orange border */}
            <motion.div
              className="fixed inset-0 pointer-events-none z-20"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="absolute inset-0 border-8 border-chaos-orange rounded-lg"
                animate={{
                  borderColor: ['hsl(var(--chaos-orange))', 'hsl(var(--chaos-yellow))', 'hsl(var(--chaos-orange))'],
                  boxShadow: [
                    'inset 0 0 30px rgba(249, 115, 22, 0.3)',
                    'inset 0 0 60px rgba(249, 115, 22, 0.5)',
                    'inset 0 0 30px rgba(249, 115, 22, 0.3)'
                  ]
                }}
                transition={{ duration: 1, repeat: Infinity }}
              />
            </motion.div>

            {/* "X AWAY" Banner */}
            <motion.div
              className="fixed top-20 left-1/2 -translate-x-1/2 z-30 pointer-events-none"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
            >
              <motion.div
                className="bg-chaos-orange/90 text-white px-6 py-3 rounded-full font-black text-xl"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 0.5, repeat: Infinity }}
              >
                🔥 {distanceToTarget} AWAY 🔥
              </motion.div>
            </motion.div>

            {/* Heartbeat vignette effect */}
            <motion.div
              className="fixed inset-0 pointer-events-none z-10"
              style={{
                background: 'radial-gradient(ellipse at center, transparent 40%, rgba(249, 115, 22, 0.2) 100%)'
              }}
              animate={{
                opacity: [0.3, 0.6, 0.3]
              }}
              transition={{ duration: 0.8, repeat: Infinity }}
            />
          </>
        )}
      </AnimatePresence>

      <div className="max-w-4xl mx-auto space-y-6 relative z-10">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button onClick={onBack} variant="ghost" size="sm">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <div className="flex items-center gap-2 bg-primary/20 px-3 py-1 rounded-full">
            <Bot className="h-4 w-4 text-primary" />
            <span className="text-sm font-bold text-primary">Practice Mode</span>
          </div>
        </div>

        {/* Stats */}
        <Card className="glass-card p-6">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-sm text-muted-foreground font-bold">ROUND</div>
              <div className="text-3xl font-black">{currentRound}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground font-bold">TOTAL</div>
              <div className={`text-4xl font-black ${
                backgroundState === 'perfect' ? 'text-chaos-green' :
                backgroundState === 'above' ? 'text-chaos-yellow' : 'text-chaos-red'
              }`}>
                {currentTotal}
              </div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground font-bold">TARGET</div>
              <div className="text-3xl font-black text-chaos-green">{targetTotal}</div>
            </div>
          </div>
        </Card>

        {/* Round Results */}
        <AnimatePresence>
          {showResults && lastRoundResult && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
            >
              <Card className={`p-4 border-2 ${
                lastRoundResult.movedCloser ? 'border-chaos-green/50 bg-chaos-green/10' : 'border-chaos-red/50 bg-chaos-red/10'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <div className="text-xs text-muted-foreground">You</div>
                      <div className="text-2xl font-black">{lastRoundResult.playerChoice > 0 ? '+' : ''}{lastRoundResult.playerChoice}</div>
                    </div>
                    <div className="text-xl text-muted-foreground">+</div>
                    <div className="text-center">
                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <Bot className="h-3 w-3" /> AI
                      </div>
                      <div className="text-2xl font-black">{lastRoundResult.aiChoice > 0 ? '+' : ''}{lastRoundResult.aiChoice}</div>
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-muted-foreground">New Total</div>
                    <div className={`text-3xl font-black ${lastRoundResult.movedCloser ? 'text-chaos-green' : 'text-chaos-red'}`}>
                      {lastRoundResult.newTotal}
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Number Selection */}
        {!showResults && (
          <div className="space-y-4">
            <h2 className="text-center text-xl font-bold text-muted-foreground">
              Pick your number
            </h2>
            
            <div className="grid grid-cols-2 gap-6">
              {playerNumbers.map((num, index) => (
                <div key={`${currentRound}-${index}`} className="space-y-2">
                  <div className="text-center text-sm font-bold text-muted-foreground">
                    Option {index === 0 ? 'A' : 'B'}
                  </div>
                  <NumberCard
                    number={num}
                    onSelect={() => handleSelectNumber(num)}
                    disabled={hasSelected}
                  />
                </div>
              ))}
            </div>

            <p className="text-center text-sm text-muted-foreground">
              <Bot className="inline h-4 w-4 mr-1" />
              AI opponent will also pick a number
            </p>
          </div>
        )}

        {/* Round History */}
        {roundHistory.length > 0 && (
          <Card className="glass-card p-4">
            <h3 className="font-bold text-sm text-muted-foreground mb-3">Round History</h3>
            <div className="flex gap-2 flex-wrap">
              {roundHistory.map((result, i) => (
                <div
                  key={i}
                  className={`px-2 py-1 rounded text-xs font-bold ${
                    result.movedCloser 
                      ? 'bg-chaos-green/20 text-chaos-green' 
                      : 'bg-chaos-red/20 text-chaos-red'
                  }`}
                >
                  R{result.round}: {result.playerChoice > 0 ? '+' : ''}{result.playerChoice + result.aiChoice}
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}